/**
 * Task Force's analytics on the website (30 Oct 2026; the plan: docs/ANALYTICS.md, the contract:
 * packages/shared/src/analytics.ts). Google Analytics 4 (gtag.js) loads only when the server gives a Measurement ID
 * (production) and never in a "View as" tab, so tests, development and admins looking as someone else send nothing.
 *
 * - Who: the account's id as GA4's user_id, and the user properties the server works out (role, employee or outside
 *   colleague, company, team). Never a name or an email.
 * - Changes: lib/api.ts reports each write the server accepted (or refused) here, and `eventForWrite` (shared with the
 *   app) says which event it is; where the person is (the open task, else the page) is the surface.
 * - Pages and tasks opened: useTfPageViews, from the address.
 * - Everything else (search, filters, files opened, links copied, the notifications answer): `track()` where it happens.
 *
 * The last events are kept in `window.__tfEvents` (also with GA off), for the end-to-end tests and for checking.
 */
import {
  analyticsParams,
  analyticsUser,
  eventForWrite,
  type TfEventName,
  type TfEventParams,
  type TfSurface,
  type TfUserProperties,
  type TfView,
  type TfWriteContext,
} from '@attendance-pro/shared';
import { APP_VERSION } from './build';
import { viewAs } from './impersonation';

declare global {
  interface Window {
    dataLayer?: unknown[];
    __tfEvents?: Array<{ name: string; params: Record<string, string | number> }>;
  }
}

let gaId: string | null = null;
/** Whether the server said GA is on (unknown at first: events wait, then go or are dropped). */
let state: 'unknown' | 'on' | 'off' = 'unknown';
const waiting: unknown[][] = [];
let userId: string | null = null;
/** No analytics at all here: an admin viewing as someone else. */
const silent = () => !!viewAs.get();

// gtag.js reads the arguments object itself, as Google's snippet pushes it.
function gtag(..._args: unknown[]) {
  // eslint-disable-next-line prefer-rest-params
  (window.dataLayer ??= []).push(arguments);
}

let props: Record<string, string> | null = null;
/** To GA, or held until the server says whether GA is on. */
function send(...args: unknown[]) {
  if (state === 'on') gtag(...args);
  else if (state === 'unknown' && waiting.length < 100) waiting.push(args);
}

/** Loads Google Analytics once, with this Measurement ID (from /api/auth/options); none: nothing is sent. */
export function startAnalytics(id: string | null | undefined) {
  if (state !== 'unknown') return;
  if (!id || silent() || typeof document === 'undefined') {
    state = 'off';
    waiting.length = 0;
    return;
  }
  gaId = id;
  state = 'on';
  gtag('js', new Date());
  // Task Force sends its own page views (an app's pages change without a page load).
  gtag('config', id, { send_page_view: false, web_version: APP_VERSION, ...(userId ? { user_id: userId } : {}) });
  if (props) gtag('set', 'user_properties', props);
  for (const a of waiting.splice(0)) gtag(...a);
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(s);
}

/** The person, once known: their account's id and who they are in the organization. */
export function identify(me: { id: string; analytics?: TfUserProperties } | null | undefined) {
  if (silent() || !me || me.id === userId) return;
  userId = me.id;
  props = me.analytics ? analyticsUser(me.analytics) : null;
  if (state === 'on') {
    gtag('config', gaId, { user_id: me.id, send_page_view: false, web_version: APP_VERSION });
    if (props) gtag('set', 'user_properties', props);
  }
  // Just signed in: say how (the sign-in page noted it before leaving for Google, or on a password).
  const how = loginPending.take();
  if (how) track('login', { method: how });
}

/** How someone is signing in, kept across the trip to Google (sessionStorage; this tab only). */
const LOGIN_KEY = 'tf.analytics.login';
export const loginPending = {
  set(method: 'google' | 'password') {
    try {
      sessionStorage.setItem(LOGIN_KEY, method);
    } catch {
      // Private mode: the sign-in is simply not counted.
    }
  },
  take(): 'google' | 'password' | null {
    try {
      const v = sessionStorage.getItem(LOGIN_KEY);
      sessionStorage.removeItem(LOGIN_KEY);
      return v === 'google' || v === 'password' ? v : null;
    } catch {
      return null;
    }
  },
};
/** A sign-in that came back refused (the server's reason code) or failed. */
export function loginFailed(method: 'google' | 'password', reason: string) {
  loginPending.take();
  track('login_failed', { method, reason });
}

/** Sends one event (and keeps it in window.__tfEvents). Never throws. */
export function track<K extends TfEventName>(name: K, params: TfEventParams[K]) {
  try {
    if (silent()) return;
    const p = analyticsParams(params);
    const log = (window.__tfEvents ??= []);
    log.push({ name, params: p });
    if (log.length > 200) log.shift();
    send('event', name, { ...p, web_version: APP_VERSION });
  } catch {
    // Analytics never gets in the way.
  }
}

// ------------------------------------------------------------------ where the person is

/** A surface set while something is open over the page (the new-task dialog, Catch up). */
let overlay: TfSurface | null = null;
export function setOverlaySurface(s: TfSurface | null) {
  overlay = s;
}

const HOME_VIEWS: Record<string, TfSurface> = {
  inbox: 'inbox',
  my: 'my_tasks',
  assigned: 'assigned_comments',
  replies: 'replies',
  releases: 'releases',
  ai: 'ai_connect',
  catchup: 'catch_up',
};

/** The page a path is (a task open over it aside). Null: not Task Force (Settings, Attendance, the Click Up report). */
export function pageOf(pathname: string): { surface: TfSurface; view?: TfView; spaceId?: string } | null {
  if (pathname === '/taskforce' || pathname === '/taskforce/') return { surface: 'home' };
  if (pathname === '/taskforce-report') return { surface: 'tf_report' };
  if (pathname.startsWith('/f/')) return { surface: 'public_form' };
  if (pathname === '/sign-in') return { surface: 'sign_in' };
  const m = /^\/taskforce\/([^/]+)\/([^/]+)/.exec(pathname);
  if (!m) return null;
  const [, space, view] = m as unknown as [string, string, string];
  if (view === 'settings') return null;
  if (space === '-') return { surface: HOME_VIEWS[view] ?? 'other' };
  const v = (['list', 'board', 'table', 'calendar'] as const).find((x) => x === view);
  return space === 'all' ? { surface: 'all_tasks', view: v } : { surface: 'space', view: v, spaceId: space };
}

/** Where an action happens now: something open over the page, the open task, or the page. */
export function currentSurface(): TfSurface {
  if (overlay) return overlay;
  if (new URLSearchParams(location.search).get('task')) return 'task';
  return pageOf(location.pathname)?.surface ?? 'other';
}

// ------------------------------------------------------------------ Task Force's names, for the events

type Meta = {
  statuses: Array<{ id: string; name: string; groupId: string }>;
  groups: Array<{ id: string; name: string }>;
  types: Array<{ id: string; name: string }>;
  fields: Array<{ id: string; key: string; name: string }>;
  roles: Array<{ id: string; name: string }>;
  spaces: Array<{ id: string; name: string; lists?: Array<{ id: string }> }>;
};
let meta: Meta | null = null;
/** Task Force's statuses, types, fields and spaces (Taskforce.tsx sets them when they load). */
export function setAnalyticsMeta(m: Meta | null | undefined) {
  if (m) meta = m;
}
export const spaceName = (id: string | undefined) => (id ? meta?.spaces.find((s) => s.id === id)?.name : undefined);

function writeContext(): TfWriteContext {
  return {
    surface: currentSurface(),
    status: (id) => {
      const s = meta?.statuses.find((x) => x.id === id);
      return s ? { name: s.name, group: meta?.groups.find((g) => g.id === s.groupId)?.name } : undefined;
    },
    typeName: (id) => meta?.types.find((t) => t.id === id)?.name,
    fieldName: (id) => meta?.fields.find((f) => f.id === id || f.key === id)?.name,
    peopleFieldName: (id) => meta?.roles.find((r) => r.id === id)?.name,
    spaceOfList: (listId) => meta?.spaces.find((s) => s.lists?.some((l) => l.id === listId))?.name,
  };
}

// ------------------------------------------------------------------ writes, from lib/api.ts

/** The same change to the same thing within a minute counts once (a description saved as people type). */
const recent = new Map<string, number>();
const REPEAT_MS = 60_000;

/** A write to Task Force's API finished: its event, or action_failed with the error's code. */
export function afterWrite(method: string, path: string, body: unknown, error?: { code: string }) {
  try {
    if (silent() || !path.startsWith('/api/tf/')) return;
    const e = eventForWrite(method, path, body, writeContext());
    if (!e) return;
    if (error) {
      const surface = 'surface' in e.params ? e.params.surface : currentSurface();
      track('action_failed', { surface, failed_event: e.name, code: error.code });
      return;
    }
    if (e.name === 'task_update' || e.name === 'comment_update' || e.name === 'checklist_update') {
      const key = `${e.name}:${path}:${'field' in e.params ? e.params.field : 'action' in e.params ? e.params.action : ''}`;
      const now = Date.now();
      const last = recent.get(key);
      recent.set(key, now);
      if (last && now - last < REPEAT_MS) return;
      if (recent.size > 500) recent.clear();
    }
    track(e.name, e.params as never);
  } catch {
    // Analytics never gets in the way.
  }
}

// ------------------------------------------------------------------ pages and tasks opened

const TITLES: Partial<Record<TfSurface, string>> = {
  home: 'Home',
  inbox: 'Inbox',
  my_tasks: 'My Tasks',
  replies: 'Replies',
  assigned_comments: 'Assigned comments',
  releases: 'Releases',
  catch_up: 'Catch up',
  ai_connect: 'Connect an AI assistant',
  all_tasks: 'All tasks',
  space: 'Space',
  tf_report: 'TaskForce Report',
  public_form: 'Form',
  sign_in: 'Sign in',
};

/** A page of Task Force: its address without ids (one row per kind of page in GA4's reports), a plain title. */
export function trackPage(page: NonNullable<ReturnType<typeof pageOf>>) {
  if (silent()) return;
  const path =
    page.surface === 'space' || page.surface === 'all_tasks'
      ? `/taskforce/${page.surface === 'space' ? 'space' : 'all'}/${page.view ?? 'default'}`
      : location.pathname.startsWith('/f/')
        ? '/f/form'
        : location.pathname;
  const title = `${TITLES[page.surface] ?? 'Task Force'}${page.view ? ` · ${page.view[0]!.toUpperCase()}${page.view.slice(1)}` : ''}`;
  const params = analyticsParams({ surface: page.surface, view: page.view, space_name: spaceName(page.spaceId) });
  const log = (window.__tfEvents ??= []);
  log.push({ name: 'page_view', params });
  if (log.length > 200) log.shift();
  send('event', 'page_view', {
    ...params,
    page_location: `${location.origin}${path}`,
    page_title: title,
    web_version: APP_VERSION,
  });
}
